import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { useMenu } from '@/contexts/MenuContext';
import { useRecipes } from '@/contexts/RecipesContext';
import { useAuth } from '@/contexts/AuthContext';
import { Spinner } from '@/components/Spinner';
import { Link } from 'react-router-dom';
import { ShoppingCart } from 'lucide-react';
import { toast } from 'sonner';
import { format, addDays, startOfWeek, addWeeks, subWeeks, isSameDay } from 'date-fns';
import { es } from 'date-fns/locale';
import { useIsMobile } from '@/hooks/use-mobile';
import FoodSearch from '@/components/FoodSearch';
import { useMenuGeneration } from '@/hooks/useMenuGeneration';
import jsPDF from 'jspdf';
import Layout from '@/components/Layout';
import { useOptimizedCalculations } from '@/hooks/useOptimizedCalculations';

// Import optimized components
import WeeklyMenuHeader from '@/components/weekly-menu/WeeklyMenuHeader';
import LisaAdviceBox from '@/components/weekly-menu/LisaAdviceBox';
import WeeklyMenuNavigation from '@/components/weekly-menu/WeeklyMenuNavigation';
import NutritionSummary from '@/components/weekly-menu/NutritionSummary';
import CustomIngredientsTable from '@/components/weekly-menu/CustomIngredientsTable';
import MealTypeCard from '@/components/weekly-menu/MealTypeCard';

const WeeklyMenu = () => {
  const { weeklyMenu, loading, removeRecipeFromMenu, clearMenu, getMenuTotals, availableMealTypes, getMealCalorieSuggestion, addCustomIngredient, updateMenu, refreshMenu, forceUpdate } = useMenu();
  const { recipes, loading: recipesLoading } = useRecipes();
  const { user } = useAuth();
  
  const today = new Date();
  const [currentWeekStart, setCurrentWeekStart] = useState(startOfWeek(today, { weekStartsOn: 1 }));
  const [selectedDate, setSelectedDate] = useState(today);
  const [activeWeek, setActiveWeek] = useState<'current' | 'next'>('current');
  const [isLisaBoxOpen, setIsLisaBoxOpen] = useState(true);
  const [localForceUpdate, setLocalForceUpdate] = useState(0);

  // Use optimized hooks
  const { isGenerating, generateDayMenu, changeMealRecipe, generatingMealType } = useMenuGeneration();
  const isMobile = useIsMobile();

  // Get correct nutrition targets from the optimized calculations hook
  const { nutritionTargets } = useOptimizedCalculations(user?.profile);

  // Force update when context forceUpdate changes
  useEffect(() => {
    setLocalForceUpdate(prev => prev + 1);
  }, [forceUpdate]);

  // Simplified refresh with better error handling
  const refreshData = useCallback(async () => {
    try {
      await refreshMenu();
      toast.success('Datos actualizados correctamente');
    } catch (error) {
      console.error('Error refreshing data:', error);
      toast.error('Error al actualizar los datos');
    }
  }, [refreshMenu]);

  // Enhanced profile update listener with immediate refresh
  useEffect(() => {
    const handleProfileUpdate = async (event) => {
      console.log('📢 Profile updated event received, forcing immediate menu refresh...');
      
      // Force immediate local state update
      setLocalForceUpdate(prev => prev + 1);
      
      // Wait a moment and then refresh menu data
      setTimeout(async () => {
        try {
          await refreshMenu();
          console.log('✅ Menu refreshed after profile update');
          toast.success('Menú actualizado con las nuevas características');
        } catch (error) {
          console.error('❌ Error refreshing menu after profile update:', error);
        }
      }, 500);
    };
    
    window.addEventListener('NUTRITION_PROFILE_UPDATED', handleProfileUpdate);
    
    return () => {
      window.removeEventListener('NUTRITION_PROFILE_UPDATED', handleProfileUpdate);
    };
  }, [refreshMenu]);

  const currentWeekDays = [
    { key: 'monday', name: 'Lunes', initial: 'L', date: addDays(currentWeekStart, 0) },
    { key: 'tuesday', name: 'Martes', initial: 'M', date: addDays(currentWeekStart, 1) },
    { key: 'wednesday', name: 'Miércoles', initial: 'X', date: addDays(currentWeekStart, 2) },
    { key: 'thursday', name: 'Jueves', initial: 'J', date: addDays(currentWeekStart, 3) },
    { key: 'friday', name: 'Viernes', initial: 'V', date: addDays(currentWeekStart, 4) },
    { key: 'saturday', name: 'Sábado', initial: 'S', date: addDays(currentWeekStart, 5) },
    { key: 'sunday', name: 'Domingo', initial: 'D', date: addDays(currentWeekStart, 6) }
  ];

  const nextWeekDays = [
    { key: 'monday2', name: 'Lunes', initial: 'L', date: addDays(currentWeekStart, 7) },
    { key: 'tuesday2', name: 'Martes', initial: 'M', date: addDays(currentWeekStart, 8) },
    { key: 'wednesday2', name: 'Miércoles', initial: 'X', date: addDays(currentWeekStart, 9) },
    { key: 'thursday2', name: 'Jueves', initial: 'J', date: addDays(currentWeekStart, 10) },
    { key: 'friday2', name: 'Viernes', initial: 'V', date: addDays(currentWeekStart, 11) },
    { key: 'saturday2', name: 'Sábado', initial: 'S', date: addDays(currentWeekStart, 12) },
    { key: 'sunday2', name: 'Domingo', initial: 'D', date: addDays(currentWeekStart, 13) }
  ];

  const activeDaysOfWeek = activeWeek === 'current' ? currentWeekDays : nextWeekDays;
  
  const selectedDay = activeDaysOfWeek.find(day => isSameDay(day.date, selectedDate))?.key || 
                     (activeWeek === 'current' ? 'monday' : 'monday2');
  
  const goToPreviousWeek = () => {
    setCurrentWeekStart(subWeeks(currentWeekStart, 1));
  };

  const goToNextWeek = () => {
    setCurrentWeekStart(addWeeks(currentWeekStart, 1));
  };

  useEffect(() => {
    if (activeWeek === 'current') {
      const todayInCurrentWeek = currentWeekDays.some(day => isSameDay(day.date, today));
      if (todayInCurrentWeek) {
        setSelectedDate(today);
      } else {
        setSelectedDate(currentWeekDays[0].date);
      }
    } else {
      setSelectedDate(nextWeekDays[0].date);
    }
  }, [activeWeek, currentWeekStart]);

  const mealTypeLabels: Record<string, { name: string, icon: string, tag: string }> = {
    'breakfast': { name: 'Desayuno', icon: '☕', tag: 'desayuno' },
    'morning_snack': { name: 'Media mañana', icon: '🥨', tag: 'snack' },
    'lunch': { name: 'Almuerzo', icon: '🍲', tag: 'comida' },
    'afternoon_snack': { name: 'Merienda', icon: '🍎', tag: 'meriendas' },
    'dinner': { name: 'Cena', icon: '🍽️', tag: 'cena' },
    'evening_snack': { name: 'Tentempié nocturno', icon: '🥛', tag: 'snack' }
  };

  const downloadMenuPDF = () => {
    if (!weeklyMenu?.days[selectedDay] || !selectedDay) {
      toast.error("No hay recetas para descargar en este día");
      return;
    }

    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.width;
      let yPosition = 20;
      
      const dayName = activeDaysOfWeek.find(d => d.key === selectedDay)?.name || 'Día';
      const dateString = format(selectedDate, "d 'de' MMMM 'de' yyyy", { locale: es });
      
      doc.setFontSize(18);
      doc.text(`Menú del ${dayName}`, pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 10;
      
      doc.setFontSize(12);
      doc.text(dateString, pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 20;

      for (const mealType of availableMealTypes) {
        const meals = weeklyMenu.days[selectedDay][mealType];
        if (!meals || meals.length === 0) continue;

        const mealLabel = mealTypeLabels[mealType] || { name: mealType, icon: '🍴' };
        
        doc.setFontSize(14);
        doc.text(`${mealLabel.icon} ${mealLabel.name}`, 20, yPosition);
        yPosition += 10;

        for (const meal of meals) {
          const recipe = recipes.find(r => r.id === meal.recipeId);
          if (!recipe) continue;

          if (yPosition > 250) {
            doc.addPage();
            yPosition = 20;
          }

          doc.setFontSize(12);
          doc.text(`• ${recipe.title} (Porción: x${meal.portionSize})`, 25, yPosition);
          yPosition += 8;

          if (recipe.ingredients && recipe.ingredients.length > 0) {
            doc.setFontSize(10);
            doc.text("Ingredientes:", 30, yPosition);
            yPosition += 6;

            for (const ingredient of recipe.ingredients) {
              const amount = Math.round((ingredient.amount * meal.portionSize) * 10) / 10;
              doc.text(`- ${ingredient.name}: ${amount} ${ingredient.unit}`, 35, yPosition);
              yPosition += 5;
              
              if (yPosition > 250) {
                doc.addPage();
                yPosition = 20;
              }
            }
            yPosition += 3;
          }

          if (recipe.instructions && recipe.instructions.length > 0) {
            doc.text("Preparación:", 30, yPosition);
            yPosition += 6;

            for (let i = 0; i < recipe.instructions.length; i++) {
              const instruction = recipe.instructions[i];
              const lines = doc.splitTextToSize(`${i + 1}. ${instruction}`, pageWidth - 70);
              
              for (const line of lines) {
                if (yPosition > 250) {
                  doc.addPage();
                  yPosition = 20;
                }
                doc.text(line, 35, yPosition);
                yPosition += 5;
              }
              yPosition += 2;
            }
          }
          yPosition += 8;
        }
        yPosition += 5;
      }

      if (weeklyMenu.days[selectedDay].customIngredients && 
          weeklyMenu.days[selectedDay].customIngredients.length > 0) {
        
        if (yPosition > 230) {
          doc.addPage();
          yPosition = 20;
        }
        
        doc.setFontSize(14);
        doc.text("🥗 Alimentos Adicionales", 20, yPosition);
        yPosition += 10;

        for (const ingredient of weeklyMenu.days[selectedDay].customIngredients) {
          doc.setFontSize(10);
          doc.text(`• ${ingredient.name}: ${ingredient.displayQuantity || `${ingredient.quantity} ${ingredient.unit}`}`, 25, yPosition);
          yPosition += 6;
        }
      }

      const fileName = `menu-${dayName.toLowerCase()}-${format(selectedDate, 'dd-MM-yyyy')}.pdf`;
      doc.save(fileName);
      
      toast.success("Menú descargado correctamente");
    } catch (error) {
      console.error("Error al generar PDF:", error);
      toast.error("Error al generar el PDF");
    }
  };

  const getSelectedDayKey = () => {
    const dayMapping = {
      'monday': 'monday',
      'tuesday': 'tuesday', 
      'wednesday': 'wednesday',
      'thursday': 'thursday',
      'friday': 'friday',
      'saturday': 'saturday',
      'sunday': 'sunday',
      'monday2': 'monday',
      'tuesday2': 'tuesday',
      'wednesday2': 'wednesday', 
      'thursday2': 'thursday',
      'friday2': 'friday',
      'saturday2': 'saturday',
      'sunday2': 'sunday'
    };
    return dayMapping[selectedDay] || 'monday';
  };

  if (loading || recipesLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  const menuPageInfo = {
    title: 'Menú Semanal',
    description: 'Haz clic en el botón Generar Menú Completo para obtener un menú del día personalizado ajustado a tus requerimientos y objetivos. Haz clic en Limpiar Menú para resetear y volver a empezar el menú desde 0 '
  };

  return (
    <Layout pageInfo={menuPageInfo}>
      <div className="py-8 bg-gray-50 w-full" key={`menu-${forceUpdate}-${localForceUpdate}`}>
        <div className="w-full max-w-none px-4">
          <WeeklyMenuHeader 
            onClearMenu={clearMenu}
            onDownloadPDF={downloadMenuPDF}
            onGenerateFullMenu={() => generateDayMenu(getSelectedDayKey())}
            isGenerating={isGenerating}
          />
          
          <LisaAdviceBox 
            isOpen={isLisaBoxOpen}
            onOpenChange={setIsLisaBoxOpen}
          />

          <WeeklyMenuNavigation
            currentWeekStart={currentWeekStart}
            activeWeek={activeWeek}
            selectedDate={selectedDate}
            currentWeekDays={currentWeekDays}
            nextWeekDays={nextWeekDays}
            onPreviousWeek={goToPreviousWeek}
            onNextWeek={goToNextWeek}
            onWeekChange={setActiveWeek}
            onDateSelect={setSelectedDate}
          />

          <div className="mt-8 grid grid-cols-1 gap-6">
            {weeklyMenu && selectedDay && (
              <div className="space-y-4">
                <h2 className="text-2xl font-bold text-center mb-6">
                  {activeDaysOfWeek.find(day => day.key === selectedDay)?.name} - {format(selectedDate, "d 'de' MMMM", { locale: es })}
                </h2>
                
                {availableMealTypes.map((mealType) => (
                  <MealTypeCard
                    key={`${mealType}-${selectedDay}-${forceUpdate}-${localForceUpdate}`}
                    mealType={mealType}
                    mealLabel={mealTypeLabels[mealType] || { name: mealType, icon: '🍴' }}
                    suggestedCalories={getMealCalorieSuggestion(mealType)}
                    dayName={activeDaysOfWeek.find(day => day.key === selectedDay)?.name || 'Día'}
                    selectedDate={selectedDate}
                    meals={weeklyMenu.days[selectedDay]?.[mealType] || []}
                    recipes={recipes}
                    generatingMenu={generatingMealType}
                    isMobile={isMobile}
                    onGenerateMeal={(type) => changeMealRecipe(selectedDay, type)}
                    onRemoveRecipe={removeRecipeFromMenu}
                    selectedDay={selectedDay}
                  />
                ))}
                
                <div className="mt-6">
                  <Card>
                    <CardContent className="pt-6">
                      <FoodSearch
                        onAddFood={(food) => addCustomIngredient(selectedDay, food)}
                      />
                    </CardContent>
                  </Card>
                </div>

                {weeklyMenu.days[selectedDay]?.customIngredients && 
                 weeklyMenu.days[selectedDay].customIngredients.length > 0 && (
                  <CustomIngredientsTable
                    customIngredients={weeklyMenu.days[selectedDay].customIngredients}
                    onRemoveCustomIngredient={(index) => {
                      removeRecipeFromMenu('', selectedDay, 'customIngredients', index);
                    }}
                  />
                )}
                
                {getMenuTotals(selectedDay) && nutritionTargets && (
                  <NutritionSummary
                    dayName={activeDaysOfWeek.find(day => day.key === selectedDay)?.name || 'Día'}
                    selectedDate={selectedDate}
                    dayTotals={getMenuTotals(selectedDay)}
                    targets={nutritionTargets}
                  />
                )}
              </div>
            )}
          </div>

          <div className="mt-8 text-center">
            <Link to="/lista-compra">
              <Button
                className="bg-vegana-primary hover:bg-vegana-primary/90 px-8"
              >
                <span className="flex items-center gap-2">
                  <ShoppingCart size={16} />
                  Ver Lista de Compras
                </span>
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default WeeklyMenu;
